import { useEffect, useMemo, useRef } from 'react';
import {
  collectFeaturedCollectionIds,
  collectRefs,
  type PageDocument,
  type StorefrontCollection,
  type StorefrontProduct,
} from '@aquilixshop/ui-react';
import { serializeComposed, useBuilderStore } from '../state/builderStore';
import {
  getCollectionStorefrontProducts,
  getStorefrontCollection,
  listStoreCollections,
} from '../lib/collectionsApi';

/** Joins/splits the collected ref ids — a space can't appear in an id, so it is a safe separator. */
const SEP = ' ';

/**
 * Resolve COLLECTIONS for the EDIT canvas — both halves of `collection-list`, plus the MEMBER PRODUCTS
 * `featured-collection` paints.
 *
 * The storefront fills two keys server-side and hands them to the sections: `resolved.collections` (the
 * picker refs, feature 0023 P2) and `resolved.storeCollections` (the store's ACTIVE collections, for
 * `source: 'all'`). The builder filled NEITHER — `usePickedProducts` covers products and `useResolvedMenus`
 * covers menus, but nothing covered collections. So a `collection-list` in the editor ALWAYS fell back to
 * the built-in demo grid ("Nouveautés / Meilleures ventes / Promotions"), whichever mode it was in: the
 * seller picked their real collections and the canvas kept showing three collections they don't own, while
 * a store with no collection at all was promised a grid the live storefront renders as nothing. This hook
 * closes that gap — it is the editor's counterpart to the bundle's collection reads.
 *
 * The THIRD half (added after the first two shipped) is `featured-collection`'s grid. Resolving the
 * collection made that section count as BOUND (`refRender` → 'real'), and its products come from a different
 * key — `resolved.collectionProducts` — that nothing in the builder filled. So the seller picked a collection
 * and the canvas went from a demo grid to an EMPTY one: worse than before the fix, and it looked like the
 * collection was broken. The bundle resolves the same thing server-side; here we fetch it per picked
 * collection, from the backend's own storefront read (ACTIVE members, MANUAL order, same cap).
 *
 * Design notes (mirrors `useResolvedMenus`, whose no-`cancelled` contract is deliberate):
 *  - The ref-id LIST drives the picked half, so typing in a text field re-runs nothing.
 *  - Each id is attempted at most once per session, so a deleted collection can't spin a fetch loop.
 *  - Only ACTIVE collections are merged (`getStorefrontCollection` returns null otherwise): a DRAFT pick
 *    resolves to nothing for the customer, so resolving it here would promise a card that never ships.
 *  - The store list is fetched once per session, and ONLY when a section actually reads it.
 *  - Everything cached is re-applied whenever the render context is rebuilt (theme undo, page switch,
 *    preview-product binding all mint a fresh context that drops `resolved`); both store actions no-op when
 *    nothing changed, so that self-healing loop settles after one pass.
 *  - A fetch is NEVER abandoned because the effect re-ran: the result goes to the global store, and
 *    `attempted` means nobody would re-fetch it — dropping it would strand the data for the whole session.
 *  - Best-effort: offline / 5xx leaves the section on its demo fallback. The canvas never throws.
 */
export function useResolvedCollections(): void {
  const order = useBuilderStore((s) => s.order);
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const meta = useBuilderStore((s) => s.meta);
  const renderContext = useBuilderStore((s) => s.renderContext);
  const mergeResolvedCollections = useBuilderStore((s) => s.mergeResolvedCollections);
  const mergeResolvedCollectionProducts = useBuilderStore((s) => s.mergeResolvedCollectionProducts);
  const setStoreCollections = useBuilderStore((s) => s.setStoreCollections);

  // COMPOSED (body + global chrome) for the same reason as `useResolvedMenus`: `serialize` is the SAVE
  // shape and omits the store-wide chrome, and a collection ref can live there too.
  const doc = useMemo(
    () => serializeComposed({ order, sectionsById, meta }),
    [order, sectionsById, meta],
  );

  const idsKey = useMemo(() => collectRefs(doc).collectionIds.join(SEP), [doc]);
  const ids = useMemo(() => (idsKey ? idsKey.split(SEP) : []), [idsKey]);
  const wantsStoreList = useMemo(() => needsStoreCollections(doc), [doc]);
  // Only the collections that actually need a product GRID — a `collection-list` of twelve cards must not
  // fire twelve member reads. Same walk the backend does when it builds the page bundle.
  const gridIdsKey = useMemo(() => collectFeaturedCollectionIds(doc).join(SEP), [doc]);
  const gridIds = useMemo(() => (gridIdsKey ? gridIdsKey.split(SEP) : []), [gridIdsKey]);

  const cache = useRef(new Map<string, StorefrontCollection>());
  const attempted = useRef(new Set<string>());
  const storeList = useRef<readonly StorefrontCollection[] | null>(null);
  const storeAttempted = useRef(false);
  const productsCache = useRef(new Map<string, readonly StorefrontProduct[]>());
  const productsAttempted = useRef(new Set<string>());

  // ── Half 1: the picked refs (`source: 'selected'`, and any other collection-bound section). ──────────
  useEffect(() => {
    if (ids.length === 0) return;

    const known = (list: string[]): Record<string, StorefrontCollection> => {
      const out: Record<string, StorefrontCollection> = {};
      for (const id of list) {
        const c = cache.current.get(id);
        if (c) out[id] = c;
      }
      return out;
    };

    // Re-apply what is already cached first (covers a context rebuild, a page switch back, an undo).
    mergeResolvedCollections(known(ids));

    const missing = ids.filter((id) => !cache.current.has(id) && !attempted.current.has(id));
    if (missing.length === 0) return;
    for (const id of missing) attempted.current.add(id);

    void Promise.all(
      missing.map(async (id) => {
        try {
          const c = await getStorefrontCollection(id);
          if (c) cache.current.set(id, c);
        } catch {
          // Deleted / offline / server error → leave the section on its fallback, never retry this id.
        }
      }),
    ).then(() => mergeResolvedCollections(known(missing)));
  }, [ids, renderContext, mergeResolvedCollections]);

  // ── Half 2: the store's own collections (`source: 'all'`). ───────────────────────────────────────────
  useEffect(() => {
    if (!wantsStoreList) return;

    // Re-apply first, so a context rebuild doesn't drop the list back to the demo grid.
    if (storeList.current) {
      setStoreCollections(storeList.current);
      return;
    }
    if (storeAttempted.current) return;
    storeAttempted.current = true;

    void (async () => {
      try {
        const list = await listStoreCollections();
        storeList.current = list;
        // An EMPTY list is a real answer, not a failure: the live storefront renders nothing for a store
        // with no collection, and the canvas must say the same instead of inventing three.
        setStoreCollections(list);
      } catch {
        // Offline / server error: allow a later edit to retry rather than sticking on demo forever.
        storeAttempted.current = false;
      }
    })();
  }, [wantsStoreList, renderContext, setStoreCollections]);

  // ── Half 3: the MEMBER PRODUCTS of each featured collection (`featured-collection`'s grid). ───────────
  useEffect(() => {
    if (gridIds.length === 0) return;

    const known = (list: string[]): Record<string, readonly StorefrontProduct[]> => {
      const out: Record<string, readonly StorefrontProduct[]> = {};
      for (const id of list) {
        const products = productsCache.current.get(id);
        // `!== undefined`, not truthiness: an EMPTY list is the answer "this collection sells nothing", and
        // dropping it here would leave the section on its demo grid forever (see the section's own note).
        if (products !== undefined) out[id] = products;
      }
      return out;
    };

    mergeResolvedCollectionProducts(known(gridIds));

    const missing = gridIds.filter(
      (id) => !productsCache.current.has(id) && !productsAttempted.current.has(id),
    );
    if (missing.length === 0) return;
    for (const id of missing) productsAttempted.current.add(id);

    void Promise.all(
      missing.map(async (id) => {
        try {
          productsCache.current.set(id, await getCollectionStorefrontProducts(id));
        } catch {
          // Deleted / offline / server error → the section keeps its demo grid, and we never retry this id.
        }
      }),
    ).then(() => mergeResolvedCollectionProducts(known(missing)));
  }, [gridIds, renderContext, mergeResolvedCollectionProducts]);
}

/**
 * Does any section on the page read `resolved.storeCollections`? Only `collection-list` does today, and
 * only outside "Selected collections" mode (`source` defaults to `'all'`, so an ABSENT value counts).
 * Gating the fetch on this keeps a page with no such section at zero extra requests.
 */
export function needsStoreCollections(doc: PageDocument): boolean {
  return doc.sections.some((s) => s.type === 'collection-list' && s.settings.source !== 'selected');
}
